package com.yogaaicoach.pages.trainingprogram

import com.yogaaicoach.scope
import kotlinx.coroutines.launch
import react.ChildrenBuilder
import react.FC
import react.Props
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.p
import react.useEffectOnce
import react.useState
import web.cssom.ClassName

internal external interface UpdateSchedulePageProps : Props {
    var viewModel: UpdateTrainingViewModel
    var onDone: () -> Unit
}

private fun Weekday.shortTitle(): String = when (this) {
    Weekday.Monday -> "Mon"
    Weekday.Tuesday -> "Tue"
    Weekday.Wednesday -> "Wed"
    Weekday.Thursday -> "Thu"
    Weekday.Friday -> "Fri"
    Weekday.Saturday -> "Sat"
    Weekday.Sunday -> "Sun"
}

private fun Weekday.fullTitle(): String = name

private fun Duration.title(): String = "${minutes} min"

internal val UpdateSchedulePage = FC<UpdateSchedulePageProps> { props ->
    val vm = props.viewModel
    var weekdays by useState(vm.weekdays.value)
    var errorText by useState(vm.scheduleErrorText.value)

    useEffectOnce {
        scope.launch { vm.weekdays.collect { weekdays = it } }
        scope.launch { vm.scheduleErrorText.collect { errorText = it } }
    }

    div {
        className = ClassName("wizard-page wizard-page-has-bottom-bar")

        PageTitleSection(
            "Set Your Training Schedule",
            "Pick the days and duration that work for you. You can always update these later.",
        )

        div {
            className = ClassName("wizard-schedule")

            if (errorText != null) {
                p { className = ClassName("wizard-schedule-error"); +errorText!! }
            }

            UpdateWeekdayPillPicker(weekdays, vm::onToggleDayChecked, vm::updateSelectedDuration)
        }
    }

    div {
        className = ClassName("wizard-bottom-bar")
        button {
            className = ClassName("outlined-button")
            disabled = weekdays.none { it.isChecked }
            onClick = { props.onDone() }
            +"Done"
        }
    }
}

private fun ChildrenBuilder.UpdateWeekdayPillPicker(
    weekdays: List<WeekdayState>,
    onDayToggled: (Weekday) -> Unit,
    onDurationUpdate: (Weekday, Duration) -> Unit,
) {
    div {
        className = ClassName("weekday-pill-row")
        weekdays.forEach { state ->
            button {
                className = ClassName(if (state.isChecked) "weekday-pill weekday-pill-checked" else "weekday-pill")
                onClick = { onDayToggled(state.weekday) }
                +state.weekday.shortTitle()
            }
        }
    }

    val selectedDays = weekdays.filter { it.isChecked }
    if (selectedDays.isNotEmpty()) {
        div { className = ClassName("weekday-duration-divider") }
        selectedDays.forEachIndexed { index, state ->
            UpdateDayDurationRow(state, onDurationUpdate)
            if (index < selectedDays.lastIndex) {
                div { className = ClassName("weekday-duration-divider") }
            }
        }
    }
}

private fun ChildrenBuilder.UpdateDayDurationRow(state: WeekdayState, onDurationUpdate: (Weekday, Duration) -> Unit) {
    div {
        className = ClassName("weekday-duration-row")
        p { className = ClassName("weekday-duration-row-title"); +state.weekday.fullTitle() }
        div {
            className = ClassName("duration-segmented-row")
            Duration.entries.forEachIndexed { index, duration ->
                button {
                    className = ClassName(
                        buildList {
                            add("duration-segment")
                            if (state.selectedDuration == duration) add("duration-segment-selected")
                            if (index == 0) add("duration-segment-first")
                            if (index == Duration.entries.lastIndex) add("duration-segment-last")
                        }.joinToString(" ")
                    )
                    onClick = { onDurationUpdate(state.weekday, duration) }
                    +duration.title()
                }
            }
        }
    }
}
